/**
 * Estate Flow - HOA Owner Dashboard
 * Dashboard for HOA homeowners to view dues, make payments, and submit maintenance requests
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { 
  Building2, DollarSign, Wrench, FileText, LogOut, 
  Calendar, CreditCard, Home, Bell, HelpCircle, Store
} from 'lucide-react';
import RentalListingManager from './landlord/RentalListingManager';

const HOAOwnerDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [showMarketplace, setShowMarketplace] = useState(false);
  const [dashboardData, setDashboardData] = useState({
    owner: {},
    unit: {},
    dues: { amount: 0, due_date: null, status: 'current' },
    recent_payments: [],
    maintenance_requests: []
  });
  const [showDocuments, setShowDocuments] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showMaintenance, setShowMaintenance] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [maintenanceForm, setMaintenanceForm] = useState({
    title: '',
    description: '',
    category: 'General',
    priority: 'medium'
  });

  useEffect(() => {
    fetchDashboardData();
    // Handle payment return — only trust server-verified payments
    const params = new URLSearchParams(window.location.search);
    if (params.get('payment') === 'success') {
      toast.success('Payment processing. Your balance will update once confirmed.');
      // Clean the URL
      window.history.replaceState({}, '', window.location.pathname);
    } else if (params.get('payment') === 'cancel') {
      toast.info('Payment was cancelled.');
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const fetchDashboardData = async () => {
    try {
      const response = await api.get('/hoa-owner/dashboard');
      setDashboardData(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error loading dashboard:', error);
      toast.error('Failed to load dashboard');
      setLoading(false);
    }
  };

  const handlePayDues = async () => {
    if (!dashboardData.dues.amount || dashboardData.dues.amount <= 0) {
      toast.error('No dues amount configured for your unit.');
      return;
    }
    try {
      const response = await api.post('/hoa-owner/payments/pay', {
        amount: dashboardData.dues.amount,
        success_url: window.location.origin + '/hoa-owner/dashboard?payment=success',
        cancel_url: window.location.origin + '/hoa-owner/dashboard?payment=cancel'
      });
      
      if (response.data.stripe_session_url) {
        window.location.href = response.data.stripe_session_url;
      } else {
        toast.error('Payment session could not be created.');
      }
    } catch (error) {
      console.error('Payment error:', error);
      const detail = error.response?.data?.detail || 'Payment processing is not available. Contact your HOA administrator.';
      toast.error(detail);
    }
  };

  const handleViewDocuments = async () => {
    try {
      const response = await api.get('/hoa-owner/documents');
      setDocuments(response.data.documents);
      setShowDocuments(true);
    } catch (error) {
      console.error('Error loading documents:', error);
      toast.error('Failed to load documents');
    }
  };

  const handleViewNotifications = async () => {
    try {
      const response = await api.get('/hoa-owner/notifications');
      setNotifications(response.data.notifications);
      setShowNotifications(true);
    } catch (error) {
      console.error('Error loading notifications:', error);
      toast.error('Failed to load notifications');
    }
  };

  const handleSubmitMaintenance = async (e) => {
    e.preventDefault();
    try {
      await api.post('/hoa-owner/maintenance', maintenanceForm);
      toast.success('Maintenance request submitted successfully');
      setShowMaintenance(false);
      setMaintenanceForm({ title: '', description: '', category: 'General', priority: 'medium' });
      fetchDashboardData(); // Refresh to show new request
    } catch (error) {
      console.error('Error submitting maintenance:', error);
      toast.error('Failed to submit request');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('user_role');
    localStorage.removeItem('user_id');
    navigate('/');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-3">
              <Building2 className="h-8 w-8 text-teal-600" />
              <div>
                <h1 className="text-2xl font-bold text-gray-900">HOA Owner Portal</h1>
                <p className="text-sm text-gray-600">{dashboardData.unit.property_name}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button 
                variant="ghost" 
                onClick={() => navigate('/kb')}
                data-testid="hoa-owner-dashboard-help-btn"
              >
                <HelpCircle className="mr-2 h-4 w-4" />
                Help
              </Button>
              <Button variant="outline" onClick={handleLogout}>
                <LogOut className="mr-2 h-4 w-4" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Your Unit</CardTitle>
              <Home className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{dashboardData.unit.unit_number || 'N/A'}</div>
              <p className="text-xs text-muted-foreground">{dashboardData.unit.property_name}</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Monthly Dues</CardTitle>
              <DollarSign className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">${dashboardData.dues.amount}</div>
              <p className="text-xs text-muted-foreground">
                Due: {dashboardData.dues.due_date ? new Date(dashboardData.dues.due_date).toLocaleDateString() : 'N/A'}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Dues Status</CardTitle>
              <FileText className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <Badge className={dashboardData.dues.status === 'current' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                {dashboardData.dues.status === 'current' ? 'Current' : 'Overdue'}
              </Badge>
              <p className="text-xs text-muted-foreground mt-2">Balance: $0.00</p>
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
          <Button className="h-24 bg-teal-600 hover:bg-teal-700" onClick={handlePayDues}>
            <div className="flex flex-col items-center">
              <CreditCard className="h-6 w-6 mb-2" />
              <span>Pay Dues</span>
            </div>
          </Button>
          <Button variant="outline" className="h-24" onClick={() => setShowMaintenance(true)}>
            <div className="flex flex-col items-center">
              <Wrench className="h-6 w-6 mb-2" />
              <span>Maintenance Request</span>
            </div>
          </Button>
          <Button variant="outline" className="h-24" onClick={handleViewDocuments}>
            <div className="flex flex-col items-center">
              <FileText className="h-6 w-6 mb-2" />
              <span>View Documents</span>
            </div>
          </Button>
          <Button variant="outline" className="h-24" onClick={handleViewNotifications}>
            <div className="flex flex-col items-center">
              <Bell className="h-6 w-6 mb-2" />
              <span>Notifications</span>
            </div>
          </Button>
          <Button 
            variant="outline" 
            className="h-24 border-emerald-200 hover:bg-emerald-50" 
            onClick={() => setShowMarketplace(!showMarketplace)}
            data-testid="hoa-marketplace-btn"
          >
            <div className="flex flex-col items-center text-emerald-600">
              <Store className="h-6 w-6 mb-2" />
              <span>List on Marketplace</span>
            </div>
          </Button>
        </div>

        {/* Leasing Hub Section */}
        {showMarketplace && (
          <div className="mb-8">
            <RentalListingManager />
          </div>
        )}

        {/* Recent Payments & Maintenance Requests */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>Payment History</span>
                <span className="text-sm text-gray-500 font-normal">Complete Records</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {dashboardData.recent_payments.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="text-left p-2 font-medium text-gray-700">Due Date</th>
                        <th className="text-left p-2 font-medium text-gray-700">Paid Date</th>
                        <th className="text-left p-2 font-medium text-gray-700">Amount</th>
                        <th className="text-left p-2 font-medium text-gray-700">Method</th>
                        <th className="text-left p-2 font-medium text-gray-700">Status</th>
                        <th className="text-left p-2 font-medium text-gray-700">Transaction ID</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dashboardData.recent_payments.map((payment, idx) => (
                        <tr key={idx} className="border-t hover:bg-gray-50">
                          <td className="p-2">
                            {payment.due_date ? (
                              new Date(payment.due_date).toLocaleDateString('en-US', { 
                                year: 'numeric', 
                                month: 'short', 
                                day: 'numeric' 
                              })
                            ) : 'N/A'}
                          </td>
                          <td className="p-2">
                            {payment.paid_date ? (
                              <span className="text-green-600 font-medium">
                                {new Date(payment.paid_date).toLocaleDateString('en-US', { 
                                  year: 'numeric', 
                                  month: 'short', 
                                  day: 'numeric' 
                                })}
                              </span>
                            ) : (
                              <span className="text-gray-400">Not paid</span>
                            )}
                          </td>
                          <td className="p-2 font-bold">${payment.amount?.toFixed(2) || '0.00'}</td>
                          <td className="p-2 capitalize">{payment.payment_method || payment.method || '-'}</td>
                          <td className="p-2">
                            <Badge className={
                              payment.status === 'paid' ? 'bg-green-100 text-green-800' :
                              payment.status === 'unpaid' ? 'bg-red-100 text-red-800' :
                              payment.status === 'late' ? 'bg-orange-100 text-orange-800' :
                              'bg-gray-100 text-gray-800'
                            }>
                              {payment.status || 'Paid'}
                            </Badge>
                          </td>
                          <td className="p-2 text-xs text-gray-500">
                            {payment.transaction_id || payment.receipt_number || 'N/A'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  
                  {/* Summary Box */}
                  <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <div className="text-sm space-y-1">
                      <div className="flex justify-between">
                        <span className="text-gray-700">Total Payments:</span>
                        <span className="font-bold">{dashboardData.recent_payments.length} records</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-700">Total Paid:</span>
                        <span className="font-bold text-green-600">
                          ${dashboardData.recent_payments
                            .filter(p => p.status === 'paid' || !p.status)
                            .reduce((sum, p) => sum + (p.amount || 0), 0)
                            .toFixed(2)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-700">Outstanding:</span>
                        <span className="font-bold text-red-600">
                          ${dashboardData.recent_payments
                            .filter(p => p.status === 'unpaid')
                            .reduce((sum, p) => sum + (p.amount || 0), 0)
                            .toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <p className="text-gray-500 text-center py-4">No payment history available</p>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Maintenance Requests</CardTitle>
            </CardHeader>
            <CardContent>
              {dashboardData.maintenance_requests.length > 0 ? (
                <div className="space-y-4">
                  {dashboardData.maintenance_requests.map((request, idx) => (
                    <div key={idx} className="flex justify-between items-center border-b pb-2">
                      <div>
                        <p className="font-medium">{request.title}</p>
                        <p className="text-sm text-gray-500">{request.date}</p>
                      </div>
                      <Badge>{request.status}</Badge>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-4">No maintenance requests</p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Documents Modal */}
        {showDocuments && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <Card className="w-full max-w-2xl max-h-[80vh] overflow-y-auto">
              <CardHeader>
                <CardTitle>HOA Documents</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {documents.map((doc, idx) => (
                    <div key={idx} className="flex justify-between items-center border-b pb-2">
                      <div>
                        <p className="font-medium">{doc.name}</p>
                        <p className="text-sm text-gray-500">Uploaded: {doc.uploaded_at}</p>
                      </div>
                      <Button variant="outline" size="sm" onClick={() => window.open(doc.url, '_blank')}>
                        View
                      </Button>
                    </div>
                  ))}
                </div>
                <div className="mt-4">
                  <Button variant="outline" onClick={() => setShowDocuments(false)}>Close</Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Notifications Modal */}
        {showNotifications && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <Card className="w-full max-w-2xl max-h-[80vh] overflow-y-auto">
              <CardHeader>
                <CardTitle>Notifications</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {notifications.map((notif) => (
                    <div key={notif.id} className="border-b pb-3">
                      <div className="flex justify-between items-start mb-2">
                        <h4 className="font-semibold">{notif.title}</h4>
                        <Badge className={notif.is_read ? 'bg-gray-100 text-gray-800' : 'bg-blue-100 text-blue-800'}>
                          {notif.is_read ? 'Read' : 'New'}
                        </Badge>
                      </div>
                      <p className="text-gray-700 mb-2">{notif.message}</p>
                      <p className="text-xs text-gray-500">
                        {new Date(notif.created_at).toLocaleString()}
                      </p>
                    </div>
                  ))}
                </div>
                <div className="mt-4">
                  <Button variant="outline" onClick={() => setShowNotifications(false)}>Close</Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Maintenance Request Modal */}
        {showMaintenance && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <Card className="w-full max-w-lg">
              <CardHeader>
                <CardTitle>Submit Maintenance Request</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmitMaintenance} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Title *</label>
                    <input
                      type="text"
                      className="w-full px-3 py-2 border rounded-md"
                      value={maintenanceForm.title}
                      onChange={(e) => setMaintenanceForm({...maintenanceForm, title: e.target.value})}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Description *</label>
                    <textarea
                      className="w-full px-3 py-2 border rounded-md"
                      rows={4}
                      value={maintenanceForm.description}
                      onChange={(e) => setMaintenanceForm({...maintenanceForm, description: e.target.value})}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Category</label>
                    <select
                      className="w-full px-3 py-2 border rounded-md"
                      value={maintenanceForm.category}
                      onChange={(e) => setMaintenanceForm({...maintenanceForm, category: e.target.value})}
                    >
                      <option value="General">General</option>
                      <option value="Plumbing">Plumbing</option>
                      <option value="Electrical">Electrical</option>
                      <option value="HVAC">HVAC</option>
                      <option value="Appliance">Appliance</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Priority</label>
                    <select
                      className="w-full px-3 py-2 border rounded-md"
                      value={maintenanceForm.priority}
                      onChange={(e) => setMaintenanceForm({...maintenanceForm, priority: e.target.value})}
                    >
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                      <option value="urgent">Urgent</option>
                    </select>
                  </div>
                  <div className="flex gap-2">
                    <Button type="submit" className="bg-teal-600 hover:bg-teal-700">Submit Request</Button>
                    <Button type="button" variant="outline" onClick={() => setShowMaintenance(false)}>Cancel</Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>
        )}
      </main>
    </div>
  );
};

export default HOAOwnerDashboard;
